import { Spinner } from '@/components/glitch-ui/spinner';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from '@tanstack/react-router';
import { useAtomValue, useAtom } from 'jotai';
import { Play, Pause, AlertCircle, Eye, CornerDownRight, Archive, CircleStop } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { selectedAdTabAtom, isLoadingCampaignsAtom, selectedAdIdAtom, rebuildingAdIdAtom, campaignDataAtom, campaignSidebarPageAtom, googleAdsAccountAtom, organizationDataAtom, transitioningCampaignIdAtom, campaignTransitionInitiatorAtom } from '@/store';
import type { Ad, CampaignData, MetaCampaignResponse } from '@/lib/types';
import { CAMPAIGN_STATE, getCampaignType, getCampaignTypeLabel, META_CAMPAIGN_TYPE_LABEL, metaCampaignDisplayName } from '@/lib/types';
import googleAds from '/google-ads.svg';
import metaLogo from '/meta.png';
import { campaignTaskTrackingQueryOptions, campaignsIncludingExternalQueryOptions, metaCampaignDetailQueryOptions, metaCampaignsQueryOptions, metaCampaignTaskStatusQueryOptions, metaIntegrationStatusQueryOptions } from '@/lib/query-options';
import { adDisplayName } from '@/lib/ad-display-name';
import { hasCampaignEnded } from '@/lib/campaign-ended';
import { hasUndeployedChanges, isAwaitingFirstPublish } from '@/lib/campaign-publish-state';
import { matchesSidebarStatus } from '@/lib/campaign-sidebar-status';
import { NON_GENERATION_TASK_KEYS } from '@/hooks/useAreAllTasksCompleted';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { SidebarContainer } from '@/components/glitch-ui/sidebar-container';
import { Pagination } from '@/components/ui/pagination';
import { AdPreviewModal } from '@/components/campaign/ads/ad-preview-modal';

/** Filter pills on the campaigns toolbar — Dave's "different tabs on the right side". See #4173. */
export type PlatformFilter = 'all' | 'google' | 'meta';

/**
 * Unified row type for the sidebar. Google and Meta campaign shapes differ enough that a
 * discriminated union keeps each row's rendering / click-handling cleanly typed. See #4173.
 */
type SidebarRow =
  | { platform: 'google'; campaign: CampaignData }
  | { platform: 'meta'; campaign: MetaCampaignResponse };

const CAMPAIGNS_PER_PAGE = 10;

interface CampaignSidebarItemProps {
  campaign: CampaignData;
  isActive: boolean;
  isCollapsed: boolean;
  onCampaignClick: (campaignSlug: string) => void;
  currencySymbol: string;
  selectedAdId: number | null;
  rebuildingAdId: number | null;
  onAdClick: (adId: number, campaignSlug: string) => void;
  onAdPreview: (adId: number) => void;
  activeCampaignAds?: any[]; // Ads from the fully-loaded selected campaign
}

const CampaignSidebarItem = ({ campaign, isActive, isCollapsed, onCampaignClick, currencySymbol, selectedAdId, rebuildingAdId, onAdClick, onAdPreview, activeCampaignAds }: CampaignSidebarItemProps) => {
  const queryClient = useQueryClient();
  const wasGeneratingRef = useRef(false);
  
  // Fetch task tracking data for this specific campaign
  const { data: taskTracking } = useQuery({
    ...campaignTaskTrackingQueryOptions(campaign.id),
    enabled: campaign.state === CAMPAIGN_STATE.DRAFT
  });
  
  
  // Detect generation failure for DRAFT campaigns. Mirrors the detail-page logic at
  // routes/_authenticated/_layout/campaigns/google/$campaignslug/_slug/index.tsx — failed or stuck
  // tasks count as a failure unless an active retry is in flight.
  // No is_stale guard here on purpose: a campaign that failed days ago is also "stale" per
  // backend, but the detail-page failed banner still fires for it — sidebar must match.
  const hasGenerationFailed = Boolean(
    campaign.state === CAMPAIGN_STATE.DRAFT &&
    taskTracking &&
    (() => {
      const campaignCreation = (taskTracking as { campaign_creation?: {
        failed?: number;
        stuck_in_progress?: number;
        in_progress?: number;
        restart_attempt_count?: number;
      } }).campaign_creation;
      if (!campaignCreation) return false;
      const failed = campaignCreation.failed ?? 0;
      const stuck = campaignCreation.stuck_in_progress ?? 0;
      const inProgress = campaignCreation.in_progress ?? 0;
      const restartAttempts = campaignCreation.restart_attempt_count ?? 0;
      // An active retry (in_progress with restart history) is not a terminal failure.
      const isActivelyRetrying = inProgress > 0 && restartAttempts > 0;
      return (failed > 0 || stuck > 0) && !isActivelyRetrying;
    })()
  );

  // Determine if campaign is being generated based on its own task tracking data.
  // hasGenerationFailed wins — a failed-and-empty campaign would otherwise match the
  // isEmpty branch below and keep showing the "generating" spinner forever.
  const isBeingGenerated = !hasGenerationFailed && Boolean(campaign.state === CAMPAIGN_STATE.DRAFT &&
    taskTracking &&
    !taskTracking.is_stale &&
    (() => {
      const { sumTotal, taskProcessing } = Object.entries(taskTracking).reduce(
        (acc, [key, value]) => {
          // Publish/sync tasks are not generation — a queued launch or Google Ads sync
          // must not flip the name back to "Generating Campaign" (#5664).
          if (NON_GENERATION_TASK_KEYS.includes(key)) return acc;
          if (typeof value === 'object' && value !== null && 'total' in value && 'in_progress' in value) {
            // Subtract orphaned IN_PROGRESS rows (worker died before persisting completion)
            // so a fresh campaign with a stuck task — still inside the 1h is_stale window —
            // doesn't permanently render the "generating" spinner on its sidebar item.
            const rawStuck = (value as { stuck_in_progress?: number }).stuck_in_progress;
            const stuck = typeof rawStuck === 'number' ? rawStuck : 0;
            acc.sumTotal += value.total;
            acc.taskProcessing += Math.max(0, value.in_progress - stuck);
          }
          return acc;
        },
        { sumTotal: 0, taskProcessing: 0 }
      );
      // Check if campaign is truly empty (no content) vs ready (has copied content)
      const isEmpty = sumTotal === 0 && (
        (!campaign.ads || campaign.ads.length === 0) &&
        (!campaign.site_links || campaign.site_links.length === 0) &&
        (!campaign.callouts || campaign.callouts.length === 0) &&
        (!campaign.phone_numbers || campaign.phone_numbers.length === 0)
      );


      return taskProcessing > 0 || isEmpty;
    })());
  
  
  // Invalidate campaigns cache when generation completes
  useEffect(() => {
    if (wasGeneratingRef.current && !isBeingGenerated) {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.slug] });
    }
    wasGeneratingRef.current = isBeingGenerated;
  }, [isBeingGenerated, queryClient, campaign.slug]);
  
  const displayName = isBeingGenerated 
    ? 'Generating Campaign'
    : campaign.name;
  
  // Use direct campaign fields for type and location
  const campaignType = getCampaignType(campaign);
  const type = getCampaignTypeLabel(campaignType);
  
  
  // Get campaign state icon
  const transitioningCampaignId = useAtomValue(transitioningCampaignIdAtom);
  const transitionInitiators = useAtomValue(campaignTransitionInitiatorAtom);
  const getStateIcon = (state: string) => {
    // Archived campaigns
    if (campaign.is_deleted) {
      return <Archive className='w-4 h-4 text-gray-400' />;
    }

    // Check if campaign is transitioning state (pause/resume/launch). The initiator atom
    // covers dispatch THROUGH task completion — the backend commits the new state at task
    // start, so without it the play/pause icon flips seconds before the transition is
    // actually done (#5669). transitioningCampaignIdAtom still covers the pre-dispatch gap.
    if (transitioningCampaignId === campaign.id || transitionInitiators[campaign.id]) {
      // Spinner takes the colour of the action in flight (#5829): green when going live,
      // amber when pausing — matching the destination state's icon colour below.
      const transitionTarget = transitionInitiators[campaign.id]?.target;
      const spinnerColour =
        transitionTarget === CAMPAIGN_STATE.LIVE ? 'text-green-500'
        : transitionTarget === CAMPAIGN_STATE.PAUSED ? 'text-amber-500'
        : 'text-[#290BD9]';
      return <Spinner size="sm" className={spinnerColour} />;
    }

    // Failed-generation surfaces as a red ! before falling through to the state switch,
    // which would otherwise render the DRAFT amber ! (visually indistinguishable from a normal
    // unpublished campaign).
    if (hasGenerationFailed) {
      return <AlertCircle className='w-4 h-4 text-red-500' />;
    }

    // Check if campaign is being generated (DRAFT state with tasks in progress).
    // Cobalt to match the "Generating Campaign" banner's spinner (#5829).
    if (isBeingGenerated) {
      return <Spinner size="sm" className="text-[#290BD9]" />;
    }

    // Special case: Live campaign with any changes pending (budget, assets, or settings). A campaign found in
    // Google Ads and not adopted never has any (#6612).
    if (hasUndeployedChanges(campaign)) {
      return <AlertCircle className='w-4 h-4 text-amber-500' />;
    }

    // A campaign Google still reports as ENABLED after its end date has run out (#6622).
    if (hasCampaignEnded(campaign)) {
      return <CircleStop className='w-4 h-4 text-gray-400' />;
    }

    switch (state) {
      case CAMPAIGN_STATE.LIVE:
        return <Play className='w-4 h-4 text-green-500' />;
      case CAMPAIGN_STATE.PAUSED:
        // Show warning for never-published campaigns (ready to go live), pause for previously-published and for
        // campaigns found in Google Ads, which already ran there (#6612)
        return isAwaitingFirstPublish(campaign)
          ? <AlertCircle className={`w-4 h-4 text-amber-500 ${!isActive ? 'animate-vibrate' : ''}`} />
          : <Pause className='w-4 h-4 text-amber-500' />;
      case CAMPAIGN_STATE.DRAFT:
        // Show warning for never-published campaigns (ready to go live), warning for previously-published
        return isAwaitingFirstPublish(campaign)
          ? <AlertCircle className={`w-4 h-4 text-amber-500 ${!isActive ? 'animate-vibrate' : ''}`} />
          : <AlertCircle className='w-4 h-4 text-amber-500' />;
      case CAMPAIGN_STATE.ERROR:
        return <AlertCircle className='w-4 h-4 text-red-500' />;
      default:
        return null;
    }
  };

  // Get campaign state color and capitalize first letter
  const getStateDisplay = (state: string) => {
    if (hasGenerationFailed) {
      return { text: 'Failed', color: 'text-red-500' };
    }

    // Check if campaign is being generated (DRAFT state with tasks in progress)
    if (isBeingGenerated) {
      return { text: 'Creating', color: 'text-amber-500' };
    }

    // Special case: Live campaign with any changes pending (budget, assets, or settings)
    if (hasUndeployedChanges(campaign)) {
      return { text: 'Live', color: 'text-amber-500' };
    }

    // Its schedule in Google Ads has run out, so it is not paused, it is over (#6622).
    if (hasCampaignEnded(campaign)) {
      return { text: 'Ended', color: 'text-gray-400' };
    }
    
    const capitalizedState = state === CAMPAIGN_STATE.DRAFT 
      ? 'Ready' 
      : state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
    
    const stateColors = {
      [CAMPAIGN_STATE.LIVE]: 'text-green-500',
      [CAMPAIGN_STATE.PAUSED]: 'text-amber-500', 
      [CAMPAIGN_STATE.DRAFT]: 'text-amber-500',
      [CAMPAIGN_STATE.ERROR]: 'text-red-500'
    };
    
    const color = stateColors[state as keyof typeof stateColors] || 'text-gray-500';
    
    return { text: capitalizedState, color };
  };
  
  const stateDisplay = campaign.is_deleted
    ? { text: 'Archived', color: 'text-gray-400' }
    : campaign.state ? getStateDisplay(campaign.state) : null;

  const getAdPath = (url: string) => {
    try {
      const parsed = new URL(url);
      const pathname = parsed.pathname.replace(/(.)\/$/, '$1');
      return pathname === '/' && !parsed.search ? 'Home' : pathname + parsed.search;
    } catch {
      return url;
    }
  };

  return (
    <div className="m-1">
      <div
        onClick={() => onCampaignClick(campaign.slug)}
        className={`rounded-md transition-colors cursor-pointer ${
          isActive && !selectedAdId ? 'bg-[#290BD9]/10' : 'hover:bg-gray-50'
        }`}
      >
        <div className={`p-2 flex items-start gap-2 ${campaign.is_deleted ? 'opacity-60' : ''}`}>
          {/* Left icon - Platform logo (replaces the old megaphone) */}
          <img src={googleAds} alt="Google Ads" className="w-4 h-4 flex-shrink-0 mt-0.5" />
          {/* Middle content */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center min-w-0 gap-1">
              <span className="text-gray-900 font-medium truncate">{displayName}</span>
            </div>
            {(campaign.website_url || type || ((campaign.budget_state === 'approved' || campaign.state === CAMPAIGN_STATE.LIVE) && campaign.budget !== undefined && campaign.budget !== null && campaign.budget > 0)) && (
              <div className="text-sm text-muted-foreground flex items-center mt-0.5">
                {campaign.website_url && <span className="truncate">{campaign.website_url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '')}</span>}
                {campaign.website_url && (type || ((campaign.budget_state === 'approved' || campaign.state === CAMPAIGN_STATE.LIVE) && campaign.budget !== undefined && campaign.budget !== null && campaign.budget > 0)) && <span className="mx-1 flex-shrink-0">•</span>}
                {type && <span className="flex-shrink-0">{type}</span>}
                {type && ((campaign.budget_state === 'approved' || campaign.state === CAMPAIGN_STATE.LIVE) && campaign.budget !== undefined && campaign.budget !== null && campaign.budget > 0) && <span className="mx-1 flex-shrink-0">•</span>}
                {(campaign.budget_state === 'approved' || campaign.state === CAMPAIGN_STATE.LIVE) && campaign.budget !== undefined && campaign.budget !== null && campaign.budget > 0 && (
                  <span className="flex-shrink-0">{currencySymbol}{campaign.budget}/d</span>
                )}
              </div>
            )}
          </div>
          {/* Right icon - Status (Google Ads logo moved left into the platform slot).
              Shown for external campaigns too since their state is now controllable (#6381). */}
          {stateDisplay && (
            <div className={`${stateDisplay.color} flex-shrink-0 mt-0.5`}>
              {getStateIcon(campaign.state)}
            </div>
          )}
        </div>
      </div>

      {/* Ads list - shown when campaign is active and not collapsed */}
      {isActive && !isCollapsed && activeCampaignAds && activeCampaignAds.length > 0 && (
        <div className="ml-8 mt-0.5 space-y-0.5">
          {[...activeCampaignAds]
            .sort((a, b) => {
              const byCreated = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
              return byCreated !== 0 ? byCreated : a.id - b.id;
            })
            .map((ad, index) => {
            const adPath = getAdPath(ad.url);
            const isAdSelected = selectedAdId === ad.id;
            const isAdRebuilding = rebuildingAdId === ad.id;

            return (
              <div
                key={ad.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onAdClick(ad.id, campaign.slug);
                }}
                className={`py-1.5 px-2 rounded text-sm cursor-pointer transition-colors flex items-center justify-between ${
                  isAdSelected
                    ? 'bg-[#290BD9]/10 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <div className={`min-w-0 flex-1 flex items-center overflow-hidden ${ad.is_paused ? 'opacity-60' : ''}`}>
                  <CornerDownRight className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />
                  <span className="font-medium truncate flex-shrink-0 max-w-full">{adDisplayName(ad, index + 1)}</span>
                  {isAdRebuilding && <Spinner size="sm" className="ml-1.5 text-[#290BD9] flex-shrink-0" />}
                  {adPath && <span className="text-gray-400 ml-auto pl-1.5 mr-2 min-w-0 truncate" title={ad.url}>{adPath}</span>}
                </div>
                <div className="flex items-center flex-shrink-0">
                  {ad.is_paused && !isAdRebuilding && <Pause className="h-3.5 w-3.5 text-amber-500 mr-1" />}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAdPreview(ad.id);
                    }}
                    className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 -mr-[3px]"
                    title="Preview Ad"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Sidebar row for a Meta campaign. Mirrors ``CampaignSidebarItem`` (Google) in shape, but uses
 * Meta-specific fields: ``offer_url`` instead of ``website_url``, ``status`` (MetaEntityStatus) for
 * the pill, ``daily_budget_cents`` (cents) for the budget display, the Meta logo on the right.
 *
 * Click navigates to ``/campaigns/meta/{slug}`` — the unified Meta detail route added in #4173 Chunk 2.
 */
const MetaCampaignSidebarItem = ({
  campaign,
  isActive,
  currencySymbol,
  onCampaignClick,
}: {
  campaign: MetaCampaignResponse;
  isActive: boolean;
  currencySymbol: string;
  onCampaignClick: (slug: string) => void;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Google parity (#5981): the active campaign expands to list its ads. The detail query is
  // already warm from the open detail page, so this costs no extra fetch.
  const { data: metaDetail } = useQuery({
    ...metaCampaignDetailQueryOptions(campaign.slug),
    enabled: isActive,
  });
  // The ad-set tier (#6210): ad sets render as first-class children with their ads nested under
  // them — Google's Campaign → Ad Group → Ad anatomy. (Pre-#6210 the ad sets were flatMapped away.)
  const metaAdSets = isActive ? (metaDetail?.ad_sets ?? []) : [];
  const isAdRouteOpen = isActive && (location.pathname.includes('/ad/') || location.pathname.includes('/adset/'));
  // Google's re-click semantics: clicking the ACTIVE campaign again collapses/expands its ad list;
  // with an ad open it navigates back to the campaign instead. Collapse resets when the row
  // deactivates, matching Google's global reset on navigation.
  const [isCollapsed, setIsCollapsed] = useState(false);
  useEffect(() => {
    if (!isActive) setIsCollapsed(false);
  }, [isActive]);

  const handleRowClick = () => {
    if (isActive && isAdRouteOpen) {
      setIsCollapsed(false);
      onCampaignClick(campaign.slug);
    } else if (isActive) {
      setIsCollapsed((prev) => !prev);
    } else {
      setIsCollapsed(false);
      onCampaignClick(campaign.slug);
    }
  };
  // A row without a Meta-side id is a DRAFT the creation task is still building (#5981) — the
  // synchronous-create flow lands users here while generation runs. Google parity: cobalt spinner
  // + "Generating Campaign" name while building, red ! when the creation task failed. The
  // offer-keyed task status is only fetched for drafts (at most one per offer in practice).
  const isDraft = campaign.meta_campaign_id == null;
  const { data: draftTaskStatus } = useQuery({
    ...metaCampaignTaskStatusQueryOptions(isDraft ? campaign.offer_url : null),
    enabled: isDraft,
  });
  const hasGenerationFailed = isDraft && draftTaskStatus?.state === 'FAILED';
  const isBeingGenerated = isDraft && !hasGenerationFailed;

  const stateDisplay = hasGenerationFailed
    ? { icon: <AlertCircle className="w-4 h-4 text-red-500" />, color: 'text-red-500' }
    : isBeingGenerated
      ? { icon: <Spinner size="sm" className="text-[#290BD9]" />, color: 'text-[#290BD9]' }
      : getMetaStateDisplay(campaign);
  // effective_daily_budget_cents covers both budget modes (CBO campaign budget / ABO ad-set sum);
  // the raw column is NULL for ABO campaigns and would hide the chip (#5981). Google's rule for
  // showing it applies here too: only once the campaign is live (or has been) — Meta has no
  // pre-launch budget-approval state, so going live IS the approval.
  const budgetIsApproved = campaign.status === 'ACTIVE' || campaign.first_activated_at != null;
  const dailyBudget = budgetIsApproved && campaign.effective_daily_budget_cents != null
    ? `${currencySymbol}${(campaign.effective_daily_budget_cents / 100).toFixed(0)}/d`
    : null;
  const hostFromOffer = (() => {
    try {
      return new URL(campaign.offer_url).hostname.replace(/^www\./, '');
    } catch {
      return campaign.offer_url;
    }
  })();

  return (
    <div className="m-1">
      <div
        onClick={handleRowClick}
        className={`rounded-md transition-colors cursor-pointer ${
          // When an ad detail is open, only the ad row is highlighted (mirrors Google's
          // `isActive && !selectedAdId` rule) so both rows don't light up at once (#6176).
          isActive && !isAdRouteOpen ? 'bg-[#290BD9]/10' : 'hover:bg-gray-50'
        }`}
      >
        <div className="p-2 flex items-start gap-2">
          {/* Left icon - Platform logo (replaces the old megaphone) */}
          <img src={metaLogo} alt="Meta Ads" className="w-4 h-auto flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center min-w-0 gap-1">
              <span className="text-gray-900 font-medium truncate">
                {isBeingGenerated
                  ? 'Generating Campaign'
                  : metaCampaignDisplayName(campaign.name, campaign.campaign_type)}
              </span>
            </div>
            <div className="text-sm text-muted-foreground flex items-center mt-0.5">
              <span className="truncate">{hostFromOffer}</span>
              {/* "host • Type • €X/d" — the exact subtitle shape Google rows render (#5981). */}
              <span className="mx-1 flex-shrink-0">•</span>
              <span className="flex-shrink-0">{META_CAMPAIGN_TYPE_LABEL[campaign.campaign_type] ?? campaign.campaign_type}</span>
              {dailyBudget && (
                <>
                  <span className="mx-1 flex-shrink-0">•</span>
                  <span className="flex-shrink-0">{dailyBudget}</span>
                </>
              )}
            </div>
          </div>
          {/* Right icon - Status (Meta logo moved left into the platform slot) */}
          {stateDisplay && (
            <div className={`${stateDisplay.color} flex-shrink-0 mt-0.5`}>
              {stateDisplay.icon}
            </div>
          )}
        </div>
      </div>

      {/* Ad-set tier with nested ads (#6210) — shown when the campaign is active and not collapsed,
          mirroring Google's ad-group children (#5981). */}
      {isActive && !isCollapsed && metaAdSets.length > 0 && (
        <div className="ml-8 mt-0.5 space-y-0.5">
          {metaAdSets.map((adSet) => {
            const isAdSetSelected = location.pathname.endsWith(`/adset/${adSet.id}`);
            return (
              <div key={adSet.id}>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate({
                      to: '/campaigns/meta/$metaCampaignSlug/adset/$adSetId',
                      params: { metaCampaignSlug: campaign.slug, adSetId: String(adSet.id) },
                    });
                  }}
                  className={`py-1.5 px-2 rounded text-sm cursor-pointer transition-colors flex items-center justify-between ${
                    isAdSetSelected ? 'bg-[#290BD9]/10 text-gray-900' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="min-w-0 flex-1 flex items-center">
                    <CornerDownRight className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />
                    <span className="font-medium truncate">{adSet.name}</span>
                  </div>
                </div>
                {adSet.ads.length > 0 && (
                  <div className="ml-5 space-y-0.5">
                    {adSet.ads.map((ad) => {
                      const isAdSelected = location.pathname.endsWith(`/ad/${ad.id}`);
                      return (
                        <div
                          key={ad.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate({
                              to: '/campaigns/meta/$metaCampaignSlug/ad/$adId',
                              params: { metaCampaignSlug: campaign.slug, adId: String(ad.id) },
                            });
                          }}
                          className={`py-1.5 px-2 rounded text-sm cursor-pointer transition-colors flex items-center justify-between ${
                            isAdSelected ? 'bg-[#290BD9]/10 text-gray-900' : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <div className="min-w-0 flex-1 flex items-center">
                            <CornerDownRight className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />
                            <span className="truncate">{ad.name}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** Map a Meta campaign's state → icon + colour pill. Mirrors the Google state pill mapping. */
function getMetaStateDisplay(campaign: MetaCampaignResponse) {
  switch (campaign.status) {
    case 'ACTIVE':
      return { icon: <Play className="w-4 h-4 text-green-500" />, color: 'text-green-500' };
    case 'PAUSED':
      // Google's rule (#5981): a PAUSED campaign that has NEVER been live is "ready to go live"
      // (amber alert), not "paused" — the pause icon is reserved for campaigns paused after
      // launching. first_activated_at is the Meta sibling of Campaign.first_published_at.
      return campaign.first_activated_at
        ? { icon: <Pause className="w-4 h-4 text-amber-500" />, color: 'text-amber-500' }
        : { icon: <AlertCircle className="w-4 h-4 text-amber-500" />, color: 'text-amber-500' };
    case 'ARCHIVED':
      return { icon: <AlertCircle className="w-4 h-4 text-gray-400" />, color: 'text-gray-400' };
    case 'DELETED':
      return { icon: <AlertCircle className="w-4 h-4 text-red-500" />, color: 'text-red-500' };
    default:
      return null;
  }
}

interface CampaignSidebarProps {
  filterTypes?: Set<string>;
  filterStatuses?: Set<string>;
  /** Platform-filter pills on the toolbar — see ``PlatformFilter``. Defaults to ``'all'``. */
  platformFilter?: PlatformFilter;
}

export function CampaignSidebar({ filterTypes, filterStatuses, platformFilter = 'all' }: CampaignSidebarProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();

  // The campaigns view is the one allowlisted surface that may show external
  // campaigns, so the sidebar fetches with include_external=true. The default
  // /organizations/{id}/campaigns response (used by the global atom) excludes
  // externals, keeping every other surface externals-free. See #3649.
  const organizationData = useAtomValue(organizationDataAtom);
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const includeArchived = filterStatuses?.has('archived') ?? false;
  const { data: campaignsData = [], isLoading: isFetchingCampaigns } = useQuery({
    ...campaignsIncludingExternalQueryOptions(
      organizationData?.id,
      includeArchived,
      googleAdsAccount?.googleads_account_id ?? null,
    ),
    // The campaign page remounts per campaign (key={campaignslug}) and the
    // filter set hydrates from saved settings after first paint, changing
    // this query's key. Without previous-data both read as cache misses, so
    // every switch blanked the sidebar to "Loading campaigns..." and made
    // the user wait for a list they already had.
    // #6450: previous data is kept ONLY while the org and account are
    // unchanged (key indexes 1 and 4) — an org/account switch must never
    // paint the previous scope's campaigns while the new list loads.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey?.[1] === organizationData?.id &&
      previousQuery?.queryKey?.[4] === (googleAdsAccount?.googleads_account_id ?? null)
        ? previousData
        : undefined,
  });
  const { data: metaCampaignsData = [], isLoading: isFetchingMetaCampaigns } = useQuery({
    ...metaCampaignsQueryOptions(undefined, organizationData?.id ?? null),
    // #6450: same org guard as above — the org id sits at key index 2.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey?.[2] === (organizationData?.id ?? null) ? previousData : undefined,
  });
  // Read the active Meta ad account's currency so Meta sidebar rows render their native symbol —
  // the org default would be wrong when org=USD but the connected Meta account is EUR/NGN. Same
  // shape ``meta-campaign-detail-panel.tsx`` uses (PR #4174 review).
  const { data: metaIntegrationStatus } = useQuery(metaIntegrationStatusQueryOptions());
  const globalLoading = useAtomValue(isLoadingCampaignsAtom);
  const isLoadingCampaigns = globalLoading || isFetchingCampaigns || isFetchingMetaCampaigns;

  const selectedAdTab = useAtomValue(selectedAdTabAtom);
  const [selectedAdId, setSelectedAdId] = useAtom(selectedAdIdAtom);
  const rebuildingAdId = useAtomValue(rebuildingAdIdAtom);
  // Get the full selected campaign data (includes ads)
  const selectedCampaignData = useAtomValue(campaignDataAtom);
  // Ad preview opened from the eye icon. Local state on purpose (#5806): setting
  // selectedAdIdAtom / showAdPreviewModalAtom here made the campaign page redirect
  // to the ad route just to show the modal. This opens it in place instead.
  const [previewAd, setPreviewAd] = useState<Ad | null>(null);
  // Track which campaign has collapsed ads (null = none collapsed, show ads for active)
  const [collapsedCampaignSlug, setCollapsedCampaignSlug] = useState<string | null>(null);

  const { symbol } = useCurrencySymbol();
  // Meta ad accounts can be in a different currency than the org default (org might be USD, Meta
  // account EUR / GBP / NGN). Use the selected ad account's currency for Meta rows so the sidebar
  // matches what the detail panel shows. glitchops-bot flagged this on PR #4174 — the detail panel's
  // own ``useCurrencySymbol(undefined, …)`` resolves the same way; this brings the sidebar into line.
  const { symbol: metaCurrencySymbol } = useCurrencySymbol(
    undefined,
    metaIntegrationStatus?.selected_ad_account?.currency,
  );

  const handleAdClick = (adId: number, campaignSlug: string) => {
    // Don't set selectedAdId here — the ad route drives it (AdPage syncs it from the URL param).
    // Setting it eagerly advances selection to the target ad BEFORE the unsaved-changes blocker
    // can fire, so the prompt's Save targets the wrong (clean) ad and a Cancel leaves selection
    // out of sync with the URL. Navigate only; selection follows the committed route. #5598.
    navigate({
      to: `/campaigns/google/${campaignSlug}/ad/${adId}`,
      hash: selectedAdTab === 'overview' ? '' : selectedAdTab,
    });
  };

  const handleAdPreview = (adId: number) => {
    // The eye only renders on the active campaign's ads (activeCampaignAds), so the
    // ad is always in the loaded campaign atom. No navigation, no atom writes —
    // the preview opens in place and closing returns exactly where the user was.
    const ad = selectedCampaignData?.ads?.find((a: Ad) => a.id === adId);
    if (ad) setPreviewAd(ad);
  };
  


  
  // Memoize Google campaigns (the existing list) to prevent hook count changes. The same
  // ``filterTypes`` / ``filterStatuses`` apply to the Meta list below via a value mapping (#6182);
  // the platform-filter pills decide which platforms appear (#4173).
  const googleCampaigns = useMemo(() => {
    const campaignsList = campaignsData || [];

    // Sort campaigns by priority (live campaigns first):
    // 0. LIVE campaigns with pending changes
    // 1. LIVE campaigns (regular)
    // 2. PAUSED, never published (review & deploy)
    // 3. DRAFT / generating
    // 4. PAUSED (previously published)
    // 5. ERROR campaigns (lowest priority)
    const getSortPriority = (campaign: CampaignData) => {
      if (campaign.is_deleted) return 20;
      if (campaign.origin === 'external') return 10;
      switch (campaign.state) {
        case CAMPAIGN_STATE.LIVE: {
          return hasUndeployedChanges(campaign) ? 0 : 1;
        }
        case CAMPAIGN_STATE.PAUSED:
          return !campaign.first_published_at ? 2 : 4;
        case CAMPAIGN_STATE.DRAFT:
          return 3;
        case CAMPAIGN_STATE.ERROR:
          return 5;
        default:
          return 6;
      }
    };

    const filtered = filterTypes && filterStatuses ? campaignsList.filter(c => {
      const type = getCampaignType(c);
      const isExternal = c.origin === 'external';
      if (isExternal && !filterTypes.has('external')) return false;
      if (!isExternal && !filterTypes.has(type)) return false;
      // Archived and state: see matchesSidebarStatus.
      return matchesSidebarStatus(c, filterStatuses);
    }) : campaignsList;

    return [...filtered].sort((a, b) => {
      const priorityA = getSortPriority(a);
      const priorityB = getSortPriority(b);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // If same priority, sort by name alphabetically
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [campaignsData, filterTypes, filterStatuses]);

  // Meta campaigns: the type/status filters apply here too (#6182) — Meta's three campaign types
  // map onto the shared type values (TESTING→prospecting, SCALING→hipo, RETARGETING→retargeting,
  // matching META_CAMPAIGN_TYPE_LABEL's display names), and status maps ACTIVE→live with a
  // never-pushed row counting as draft. Then sort active first; name alphabetical within tier.
  const metaCampaigns = useMemo(() => {
    const typeValue: Record<string, string> = { TESTING: 'prospecting', SCALING: 'hipo', RETARGETING: 'retargeting' };
    const statusValue = (c: MetaCampaignResponse) =>
      c.meta_campaign_id == null
        ? 'draft'
        : ({ ACTIVE: 'live', PAUSED: 'paused', ARCHIVED: 'archived', DELETED: 'archived' }[c.status] ?? 'paused');
    const filtered =
      filterTypes && filterStatuses
        ? (metaCampaignsData || []).filter(
            (c) => filterTypes.has(typeValue[c.campaign_type] ?? c.campaign_type) && filterStatuses.has(statusValue(c)),
          )
        : metaCampaignsData || [];
    const metaSortPriority = (c: MetaCampaignResponse) => ({ ACTIVE: 1, PAUSED: 2, ARCHIVED: 3, DELETED: 4 }[c.status] ?? 5);
    return [...filtered].sort((a, b) => {
      const diff = metaSortPriority(a) - metaSortPriority(b);
      if (diff !== 0) return diff;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [metaCampaignsData, filterTypes, filterStatuses]);

  // Unified rows after platform filter. Google rows first, then Meta — preserves the existing
  // Google-priority ordering. When the user picks a specific platform, the other is excluded.
  const rows: SidebarRow[] = useMemo(() => {
    const googleRows: SidebarRow[] = googleCampaigns.map(c => ({ platform: 'google', campaign: c }));
    const metaRows: SidebarRow[] = metaCampaigns.map(c => ({ platform: 'meta', campaign: c }));
    if (platformFilter === 'google') return googleRows;
    if (platformFilter === 'meta') return metaRows;
    return [...googleRows, ...metaRows];
  }, [googleCampaigns, metaCampaigns, platformFilter]);

  const { campaignslug: routeCampaignslug, metaCampaignSlug: routeMetaSlug } = useParams({ strict: false }) as {
    campaignslug?: string;
    metaCampaignSlug?: string;
  };
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);
  // Use pending slug immediately on click for instant highlight; fall back to route param
  const campaignslug = pendingSlug ?? routeCampaignslug;
  // Clear pending slug once the route catches up
  useEffect(() => {
    if (pendingSlug && pendingSlug === routeCampaignslug) setPendingSlug(null);
  }, [routeCampaignslug, pendingSlug]);

  // The active Meta campaign slug (route param ``/campaigns/meta/{slug}``), with the same instant-
  // highlight pattern via ``pendingMetaSlug`` (#4659 — slug, not the enumerable integer id).
  const [pendingMetaSlug, setPendingMetaSlug] = useState<string | null>(null);
  const activeMetaSlug = pendingMetaSlug ?? routeMetaSlug;
  useEffect(() => {
    if (pendingMetaSlug != null && pendingMetaSlug === routeMetaSlug) {
      setPendingMetaSlug(null);
    }
  }, [routeMetaSlug, pendingMetaSlug]);

  // Pagination state (persisted in atom to survive route changes)
  const [currentPage, setCurrentPage] = useAtom(campaignSidebarPageAtom);
  const totalPages = Math.max(1, Math.ceil(rows.length / CAMPAIGNS_PER_PAGE));

  // Reset current page if it exceeds total pages (e.g. after campaign deletion)
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, setCurrentPage]);

  // Auto-navigate to the page containing the active campaign — ONLY when the
  // active campaign identity actually changes (route change / after creation),
  // never on a mere ``rows`` reference change (refetch/re-render). Otherwise it
  // clobbers a deliberate manual page click and the sidebar snaps back. #4272
  // Works for both platforms — Google by ``campaignslug``, Meta by ``activeMetaSlug`` (#4173).
  const lastActiveCampaignKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (rows.length === 0) return;
    const activeKey = campaignslug
      ? `g:${campaignslug}`
      : activeMetaSlug != null
        ? `m:${activeMetaSlug}`
        : null;
    if (activeKey === lastActiveCampaignKeyRef.current) return;
    lastActiveCampaignKeyRef.current = activeKey;
    if (activeKey === null) return;
    let activeIndex = -1;
    if (campaignslug) {
      activeIndex = rows.findIndex(r => r.platform === 'google' && r.campaign.slug === campaignslug);
    } else if (activeMetaSlug != null) {
      activeIndex = rows.findIndex(r => r.platform === 'meta' && r.campaign.slug === activeMetaSlug);
    }
    if (activeIndex >= 0) {
      const targetPage = Math.floor(activeIndex / CAMPAIGNS_PER_PAGE) + 1;
      setCurrentPage(targetPage);
    }
  }, [campaignslug, activeMetaSlug, rows, setCurrentPage]);

  // Navigate away when the active campaign is excluded by the current filters.
  // Only re-target Google rows; Meta has no per-campaign route in Chunk 1 (see #4173).
  // #5633: redirect away from a given campaignslug AT MOST ONCE (one-shot ref guard, like the page
  // effect above). Without it, a freshly-created campaign whose slug isn't yet in `rows` (e.g. the
  // creation stalled) made this fire `navigate` on every render — the route never settled, producing
  // an infinite "Maximum update depth exceeded" loop. Reset once the campaign becomes visible. The
  // pendingSlug check also avoids fighting an in-flight optimistic navigation.
  const redirectedAwayFromRef = useRef<string | null>(null);
  useEffect(() => {
    if (pendingSlug || !campaignslug || isLoadingCampaigns || rows.length === 0) return;
    const stillVisible = rows.some(r => r.platform === 'google' && r.campaign.slug === campaignslug);
    if (stillVisible) {
      redirectedAwayFromRef.current = null;
      return;
    }
    if (redirectedAwayFromRef.current === campaignslug) return;
    const firstGoogle = rows.find(r => r.platform === 'google');
    if (firstGoogle && firstGoogle.platform === 'google') {
      redirectedAwayFromRef.current = campaignslug;
      const hash = location.hash?.replace('#', '') || '';
      navigate({ to: `/campaigns/google/${firstGoogle.campaign.slug}`, hash: hash || undefined });
    }
  }, [rows, campaignslug, isLoadingCampaigns, navigate, location.hash, pendingSlug]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * CAMPAIGNS_PER_PAGE;
    return rows.slice(start, start + CAMPAIGNS_PER_PAGE);
  }, [rows, currentPage]);

  // Show the loading state only when there is genuinely nothing to show —
  // a list already in hand keeps rendering through any refetch.
  if (isLoadingCampaigns && rows.length === 0) {
    return (
      <SidebarContainer>
        <div className="p-4 text-center text-gray-500 flex items-center justify-center gap-2">
          <Spinner size="md" />
          Loading campaigns...
        </div>
      </SidebarContainer>
    );
  }

  // Empty state — wording depends on which platform filter is active.
  if (rows.length === 0) {
    return (
      <SidebarContainer>
        <div className="p-4 text-center text-gray-500">
          {platformFilter === 'meta'
            ? 'No Meta campaigns yet'
            : platformFilter === 'google'
              ? 'No Google campaigns yet'
              : 'No campaigns available'}
        </div>
      </SidebarContainer>
    );
  }
  
  // Get current hash to preserve tab when switching campaigns
  const currentHash = location.hash?.replace('#', '') || '';

  const handleCampaignClick = (campaignSlug: string) => {
    setPendingSlug(campaignSlug);
    const isActiveCampaign = campaignslug === campaignSlug;
    const hasAdSelected = selectedAdId !== null;

    if (isActiveCampaign && hasAdSelected) {
      // Case: Campaign is active but an ad is selected
      // → Clear ad selection and navigate to campaign (don't toggle collapse)
      setSelectedAdId(null);
      setCollapsedCampaignSlug(null);
      navigate({ to: `/campaigns/google/${campaignSlug}`, hash: currentHash || undefined });
    } else if (isActiveCampaign && !hasAdSelected) {
      // Case: Campaign is active and no ad is selected
      // → Toggle collapse state
      setCollapsedCampaignSlug(prev => prev === campaignSlug ? null : campaignSlug);
    } else {
      // Case: Different campaign clicked
      // → Navigate to it and reset states, preserving current tab
      setSelectedAdId(null);
      setCollapsedCampaignSlug(null);
      navigate({ to: `/campaigns/google/${campaignSlug}`, hash: currentHash || undefined });
    }
  };

  // Meta click handler — navigates to the unified ``/campaigns/meta/{slug}`` detail route added in
  // #4173 Chunk 2. Optimistic pending state keeps the highlight responsive while TanStack swaps the
  // route in (same trick as the Google ``pendingSlug``).
  const handleMetaCampaignClick = (slug: string) => {
    setPendingMetaSlug(slug);
    navigate({ to: '/campaigns/meta/$metaCampaignSlug', params: { metaCampaignSlug: slug } });
  };

  return (
    <SidebarContainer>
      <div className="w-full h-full flex flex-col">
        <div className="flex-1 overflow-y-auto">
          <nav>
            {paginatedRows.map(row => {
              if (row.platform === 'google') {
                const campaign = row.campaign;
                const isActive = campaignslug === campaign.slug;
                const isCollapsed = collapsedCampaignSlug === campaign.slug;
                return (
                  <CampaignSidebarItem
                    key={`g-${campaign.id}`}
                    campaign={campaign}
                    isActive={isActive}
                    isCollapsed={isCollapsed}
                    onCampaignClick={handleCampaignClick}
                    currencySymbol={symbol || '$'}
                    selectedAdId={selectedAdId}
                    rebuildingAdId={rebuildingAdId}
                    onAdClick={handleAdClick}
                    onAdPreview={handleAdPreview}
                    activeCampaignAds={isActive && selectedCampaignData?.slug === campaign.slug ? selectedCampaignData?.ads : undefined}
                  />
                );
              }
              // Meta row — see ``MetaCampaignSidebarItem``. ``isActive`` mirrors the route param
              // ``/campaigns/meta/{slug}`` (Chunk 2), with the same optimistic-pending trick the Google
              // rows use so the highlight is instant on click.
              return (
                <MetaCampaignSidebarItem
                  key={`m-${row.campaign.id}`}
                  campaign={row.campaign}
                  isActive={activeMetaSlug === row.campaign.slug}
                  currencySymbol={metaCurrencySymbol || symbol || '$'}
                  onCampaignClick={handleMetaCampaignClick}
                />
              );
            })}
          </nav>
        </div>
        {totalPages > 1 && (
          <div className="border-t px-2 py-2">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          </div>
        )}
      </div>
      {/* Ad preview from the eye icon — opens in place, URL unchanged (#5806). */}
      {previewAd && selectedCampaignData && (
        <AdPreviewModal
          ad={previewAd}
          campaign={selectedCampaignData}
          open={!!previewAd}
          onOpenChange={(open) => {
            if (!open) setPreviewAd(null);
          }}
        />
      )}
    </SidebarContainer>
  );
}